import { createApi } from "@reduxjs/toolkit/query/react";

import { axiosBaseQuery, ListResponse } from "@/api/base";
import {
  BusinessReport,
  BusinessReportOutcome,
  BusinessReportOutcomeDetails,
  BusinessReportOutcomePermissionAccess,
} from "./businessReports.types";
import { SnapshotSearchEvent } from "@/modules/snapshots/snapshotCollections/components/outcomes/search/types";

const TAG = "business-reports";
const TAG_OUTCOME = "business-reports-outcome";
const TAG_OUTCOME_SEARCH = "business-reports-outcome-search";

interface CreateBusinessReportPayload {
  name: string;
  description: string;
  file: FileList;
}

interface UpdateBusinessReportPayload {
  name: string;
  description: string;
  is_active: boolean;
}

export const businessReportsApi = createApi({
  baseQuery: axiosBaseQuery({ baseUrl: "/pf-business-reports/" }),
  reducerPath: "businessReportsApi",
  tagTypes: [TAG, TAG_OUTCOME, TAG_OUTCOME_SEARCH],
  refetchOnFocus: true,
  endpoints: builder => ({
    deleteBusinessReportOutcomePermission: builder.mutation<void, { id: string; permissionId: string }>({
      invalidatesTags: [TAG_OUTCOME],
      query: ({ id, permissionId }) => ({
        url: `business-report-outcome/${id}/permissions/${permissionId}`,
        method: "DELETE",
      }),
    }),
    retryBusinessReportOutcomeResult: builder.mutation<BusinessReportOutcomeDetails, { id: string; resultId: string }>({
      invalidatesTags: [TAG_OUTCOME],
      query: ({ id, resultId }) => ({
        url: `business-report-outcome/${id}/retry/${resultId}`,
        method: "POST",
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const businessReportOutcome = await queryFulfilled;
        dispatch(
          businessReportsApi.util.updateQueryData("getBusinessReportOutcome", id, draft => {
            Object.assign(draft, businessReportOutcome);
          }),
        );
      },
    }),
    updateBusinessReportOutcomeResult: builder.mutation<
      BusinessReportOutcomeDetails,
      { id: string; resultId: string; data: { result: string } }
    >({
      invalidatesTags: [TAG_OUTCOME],
      query: ({ id, resultId, data }) => ({
        url: `business-report-outcome/${id}/update/${resultId}`,
        method: "POST",
        data,
      }),
      async onQueryStarted({ id, resultId, data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          businessReportsApi.util.updateQueryData("getBusinessReportOutcome", id, draft => {
            Object.assign(draft, {
              result: draft.result.map(row => ({ ...row, result: row.id === resultId ? data.result : row.result })),
            });
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateBusinessReportOutcomeAccess: builder.mutation<
      void,
      { id: string; access: BusinessReportOutcomePermissionAccess }
    >({
      invalidatesTags: [TAG],
      query: ({ id, access }) => ({
        url: `business-report-outcome/${id}/access`,
        method: "PATCH",
        data: { access },
      }),
    }),
    createBusinessReportOutcomePermission: builder.mutation<void, { id: string; role: string; users: string[] }>({
      invalidatesTags: [TAG_OUTCOME],
      query: ({ id, role, users }) => ({
        url: `business-report-outcome/${id}/permissions`,
        method: "POST",
        data: {
          permissions: users.map(user => ({ role, user_id: user, business_report_outcome_id: id })),
        },
      }),
    }),
    createBusinessReportOutcome: builder.mutation<
      {
        snapshot_collection_id: string;
        business_report_outcome_ids: string[];
      },
      {
        fs_entries: string[];
        business_report_id: string;
        name: string;
        variables: Record<string, string>;
        snapshot_collection_id?: string;
        snapshot_collection_new?: string;
        business_report_prompts: {
          order: number;
          business_report_prompt_id: string;
          prompt: string;
          name: string;
        }[];
      }
    >({
      invalidatesTags: [TAG_OUTCOME],
      query: ({
        name,
        fs_entries,
        business_report_id,
        variables,
        business_report_prompts,
        snapshot_collection_id,
        snapshot_collection_new,
      }) => ({
        url: "business-report-outcome",
        method: "POST",
        data: {
          snapshot_collection_id,
          snapshot_collection_new,
          fs_entries,
          name,
          business_report_id,
          business_report_prompts,
          variables,
        },
      }),
    }),
    createBusinessReport: builder.mutation<
      void,
      { data: CreateBusinessReportPayload | Partial<UpdateBusinessReportPayload>; id?: string }
    >({
      invalidatesTags: [TAG],
      query: ({ data, id }) => {
        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (key === "file") {
            if (value[0]) formData.append(key, value[0]);
          } else {
            formData.append(key, value);
          }
        });

        return {
          headers: {
            "Content-Type": "multipart/form-data;",
          },
          url: id ? `business-reports/${id}` : "business-reports",
          method: id ? "PATCH" : "POST",
          data: formData,
        };
      },
    }),
    updateBusinessReport: builder.mutation<
      void,
      { data: CreateBusinessReportPayload | Partial<UpdateBusinessReportPayload>; id?: string }
    >({
      invalidatesTags: [TAG],
      query: ({ data, id }) => ({
        url: id ? `business-reports/${id}` : "business-reports",
        method: id ? "PATCH" : "POST",
        data,
      }),
    }),
    getBusinessReports: builder.query<BusinessReport[], void>({
      providesTags: [TAG],
      query: () => ({
        url: "business-report-list",
      }),
    }),
    getBusinessReportsOutcome: builder.query<
      ListResponse<BusinessReportOutcome>,
      Partial<{
        snapshot_collection_id: string;
        offset: number;
        query?: string;
        descending?: boolean;
        order_by?: string;
      }> | void
    >({
      providesTags: [TAG_OUTCOME],
      query: (params?) => ({
        url: "business-report-outcome",
        params,
      }),
    }),
    searchBusinessReportsOutcome: builder.query<
      SnapshotSearchEvent[],
      {
        business_report_id: string;
        search_id: string;
        snapshot_collection_id: string;
        query: string;
      }
    >({
      providesTags: [TAG_OUTCOME_SEARCH],
      query: params => ({
        url: "search",
        method: "POST",
        params,
      }),
    }),
    qaBusinessReportsOutcome: builder.query<
      void,
      {
        business_report_id: string;
        search_id: string;
        snapshot_collection_id: string;
        query: string;
      }
    >({
      query: params => ({
        url: "qa",
        method: "POST",
        params,
      }),
    }),
    deleteBusinessReportOutcome: builder.mutation<void, { id: string }>({
      invalidatesTags: [TAG_OUTCOME],
      query: ({ id }) => ({
        url: `business-report-outcome/${id}`,
        method: "DELETE",
      }),
    }),
    updateBusinessReportOutcome: builder.mutation<
      void,
      {
        id: string;
        data: Partial<
          {
            snapshot_collection_id?: string;
            snapshot_collection_new?: string;
          } & BusinessReportOutcome
        >;
      }
    >({
      invalidatesTags: [TAG_OUTCOME],
      query: ({ id, data }) => ({
        url: `business-report-outcome/${id}`,
        method: "PATCH",
        data,
      }),
    }),
    getBusinessReportOutcome: builder.query<BusinessReportOutcomeDetails, string>({
      providesTags: [TAG_OUTCOME],
      query: id => ({
        url: `business-report-outcome/${id}`,
      }),
    }),
  }),
});

export const {
  useLazyQaBusinessReportsOutcomeQuery: useQaBusinessReportsOutcomeQuery,
  useSearchBusinessReportsOutcomeQuery,
  useGetBusinessReportsOutcomeQuery,
  useGetBusinessReportsQuery,
  useGetBusinessReportOutcomeQuery,
  useCreateBusinessReportMutation,
  useUpdateBusinessReportMutation,
  useCreateBusinessReportOutcomeMutation,
  useDeleteBusinessReportOutcomePermissionMutation,
  useCreateBusinessReportOutcomePermissionMutation,
  useUpdateBusinessReportOutcomeMutation,
  useDeleteBusinessReportOutcomeMutation,
  useRetryBusinessReportOutcomeResultMutation,
  useUpdateBusinessReportOutcomeResultMutation,
  useUpdateBusinessReportOutcomeAccessMutation,
} = businessReportsApi;
